import { useTranslation } from 'react-i18next'
import { Mail, Calendar, User, Users, Paperclip, FileText } from 'lucide-react'
import { useState, useEffect } from 'react'

interface EmailViewerProps {
  fileUrl: string
  fileName?: string
}

interface EmailData {
  from?: string
  to?: string[]
  cc?: string[]
  subject?: string
  date?: string
  body?: string
  attachments?: Array<{ name: string; size: number }>
}

export default function EmailViewer({ fileUrl, fileName: _fileName }: EmailViewerProps) {
  const { t } = useTranslation()
  const [emailData, setEmailData] = useState<EmailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Fetch and parse email content
    fetch(fileUrl)
      .then((response) => response.text())
      .then((content) => {
        // Try to parse email headers from the content
        const parsed = parseEmailContent(content)
        setEmailData(parsed)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error loading email:', err)
        setError(t('emailViewer.errors.loadFailed'))
        setLoading(false)
      })
  }, [fileUrl, t])

  const parseEmailContent = (content: string): EmailData => {
    // Basic email parsing - this is a simplified version
    // In production, you might want to use a proper email parsing library
    const lines = content.split('\n')
    const headers: Record<string, string> = {}
    let bodyStartIndex = 0

    // Parse headers
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (line.trim() === '') {
        bodyStartIndex = i + 1
        break
      }
      const colonIndex = line.indexOf(':')
      if (colonIndex > 0) {
        const key = line.substring(0, colonIndex).trim().toLowerCase()
        const value = line.substring(colonIndex + 1).trim()
        headers[key] = value
      }
    }

    // Get body
    const body = lines.slice(bodyStartIndex).join('\n')

    return {
      from: headers['from'],
      to: headers['to']?.split(',').map((s) => s.trim()),
      cc: headers['cc']?.split(',').map((s) => s.trim()),
      subject: headers['subject'],
      date: headers['date'],
      body: body || content,
      attachments: []
    }
  }

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-'
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-base-100">
        <div className="text-center">
          <Mail className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-sm text-base-content/70">{t('emailViewer.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !emailData) {
    return (
      <div className="flex items-center justify-center h-full bg-base-100">
        <div className="text-center">
          <Mail className="w-12 h-12 text-error mx-auto mb-4" />
          <p className="text-lg font-medium text-base-content">{error || t('emailViewer.errors.loadFailed')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-base-100">
      {/* Email Header */}
      <div className="border-b border-base-300 bg-base-100">
        <div className="p-6 space-y-4">
          {/* Subject */}
          <div>
            <h2 className="text-2xl font-bold text-base-content">
              {emailData.subject || t('emailViewer.noSubject')}
            </h2>
          </div>

          {/* Metadata */}
          <div className="space-y-2">
            {/* From */}
            {emailData.from && (
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-base-content/50 mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-base-content/70">
                    {t('emailViewer.from')}:
                  </span>
                  <span className="text-sm text-base-content ml-2">{emailData.from}</span>
                </div>
              </div>
            )}

            {/* To */}
            {emailData.to && emailData.to.length > 0 && (
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 text-base-content/50 mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-base-content/70">
                    {t('emailViewer.to')}:
                  </span>
                  <span className="text-sm text-base-content ml-2">{emailData.to.join(', ')}</span>
                </div>
              </div>
            )}

            {/* CC */}
            {emailData.cc && emailData.cc.length > 0 && (
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 text-base-content/50 mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-base-content/70">
                    {t('emailViewer.cc')}:
                  </span>
                  <span className="text-sm text-base-content ml-2">{emailData.cc.join(', ')}</span>
                </div>
              </div>
            )}

            {/* Date */}
            {emailData.date && (
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-base-content/50 mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-base-content/70">
                    {t('emailViewer.date')}:
                  </span>
                  <span className="text-sm text-base-content ml-2">{formatDate(emailData.date)}</span>
                </div>
              </div>
            )}

            {/* Attachments */}
            {emailData.attachments && emailData.attachments.length > 0 && (
              <div className="flex items-start gap-3">
                <Paperclip className="w-4 h-4 text-base-content/50 mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-base-content/70">
                    {t('emailViewer.attachments')}:
                  </span>
                  <div className="mt-1 space-y-1">
                    {emailData.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-base-content/70">
                        <FileText className="w-3 h-3" />
                        <span>{attachment.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Body */}
      <div className="flex-1 overflow-auto bg-base-200 p-6">
        <div className="bg-base-100 rounded-lg shadow-lg p-6">
          <div className="prose max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-base-content bg-base-200 p-4 rounded">
              {emailData.body}
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}
